import { GuidebookV2Field } from './address-guidebook.service';
import { GUEST_ADDRESS_FIELDS, GuestAddressField } from './guidebook-address-fields';
import { GuidebookEntry } from './guidebook-entries';

/**
 * Property-record patches for the two places that edit the parking and access columns
 * (Edit Property and the guidebook section editor). Both send only what changed, and a
 * cleared value goes out as null so the server forgets it instead of keeping the old one.
 */

export type AddressFieldValue = string | number | boolean | null;

export type GuestAddressValues = Partial<Record<GuestAddressField, unknown>>;

const PRO_ADDRESS_FIELDS = ['paid_parking', 'parking_spot', 'parking_pay_with', 'max_parking_cost', 'parking_notes', 'home_access', 'home_close'] as const;

export type ProAddressValues = Partial<Record<(typeof PRO_ADDRESS_FIELDS)[number], unknown>>;

const PRO_PARKING_DEPENDENTS = ['parking_spot', 'parking_pay_with', 'max_parking_cost'] as const;

const GUEST_PARKING_DEPENDENTS = PRO_PARKING_DEPENDENTS.map(field => GUEST_ADDRESS_FIELDS[field]);

function normalizeAddressValue(value: unknown): unknown {
  return value === undefined || value === '' ? null : value;
}

/** The API can hand back cents as a string, so non-empty values compare loosely. */
export function isSameAddressValue(left: unknown, right: unknown): boolean {
  const a = normalizeAddressValue(left);
  const b = normalizeAddressValue(right);
  if (a === null || b === null) { return a === b; }
  return String(a) === String(b);
}

/**
 * Columns that changed versus the loaded address, with cleared values as null.
 * A new parking type also sends the spot and payment fields it resets, even when they
 * already looked empty, so stale details from the previous type can't survive.
 */
function buildAddressPatch<F extends string>(
  fields: readonly F[],
  parkingTypeField: F,
  parkingDependents: readonly F[],
  loaded: Partial<Record<F, unknown>> | null | undefined,
  current: Partial<Record<F, unknown>>,
): Partial<Record<F, unknown>> {
  const patch: Partial<Record<F, unknown>> = {};
  const parkingTypeChanged = !isSameAddressValue(loaded?.[parkingTypeField], current[parkingTypeField]);
  for (const field of fields) {
    const next = normalizeAddressValue(current[field]);
    const changed = !isSameAddressValue(loaded?.[field], next);
    if (changed || (parkingTypeChanged && parkingDependents.includes(field))) {
      patch[field] = next;
    }
  }
  return patch;
}

export function buildProAddressPatch(loaded: ProAddressValues | null | undefined, current: ProAddressValues): ProAddressValues {
  return buildAddressPatch(PRO_ADDRESS_FIELDS, 'paid_parking', PRO_PARKING_DEPENDENTS, loaded, current);
}

export function buildGuestAddressPatch(loaded: GuestAddressValues | null | undefined, current: GuestAddressValues): GuestAddressValues {
  return buildAddressPatch(Object.values(GUEST_ADDRESS_FIELDS), 'guest_paid_parking', GUEST_PARKING_DEPENDENTS, loaded, current);
}

/**
 * Custom Field values that changed versus the loaded address, with cleared values as null.
 * The server merges these keys into the stored ones, so unchanged fields are left out.
 */
export function buildCustomFieldsPatch(
  fieldKeys: string[],
  loaded: Record<string, unknown> | null | undefined,
  current: Record<string, unknown>,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (const key of fieldKeys) {
    const next = normalizeAddressValue(current[key]);
    if (!isSameAddressValue(loaded?.[key], next)) {
      patch[key] = next;
    }
  }
  return patch;
}

/** Category fields whose value no longer applies once the parking type changes. */
export function paidParkingDependentKeys(fields: GuidebookV2Field[]): string[] {
  return fields.filter(field => field.onlyWhenPaid || field.optionsWhenPaid).map(field => field.key);
}

/**
 * Property-record columns a guidebook category save changes. Selects compare against the
 * stored value; text fields compare against the stored text.
 */
export function buildCategoryAddressPatch(
  fields: GuidebookV2Field[],
  entries: GuidebookEntry[],
  values: Record<string, AddressFieldValue | undefined>,
): Record<string, AddressFieldValue> {
  const patch: Record<string, AddressFieldValue> = {};
  for (const field of fields) {
    const entry = entries.find(candidate => candidate.key === field.key);
    if (!entry || entry.kind !== 'address' || !entry.addressField) { continue; }
    const value = values[field.key] ?? null;
    if (field.inputType === 'select') {
      if (!isSameAddressValue(entry.rawValue, value)) {
        patch[entry.addressField] = value === '' ? null : value;
      }
    } else if ((entry.value || '') !== (value || '')) {
      patch[entry.addressField] = value || null;
    }
  }
  return patch;
}
